// URL parameter helpers (history/BrowserRouter based).
// NOTE: This module historically used hash-based URLs (/#/path?x=y).
// It now operates on real URLs (/path?x=y). Function names are kept
// unchanged for backward compatibility with existing call sites.

/**
 * Parse the current URL's path and query parameters.
 * Works with clean URLs like: /search?category=electronics&page=2
 */
export const parseHashParams = () => {
  const path = window.location.pathname;
  const search = window.location.search; // includes leading '?'
  return { path, params: new URLSearchParams(search) };
};

/**
 * Get a specific query parameter from the current URL.
 */
export const getHashParam = (paramName) => {
  const { params } = parseHashParams();
  return params.get(paramName);
};

/**
 * Get all query parameters from the current URL as a plain object.
 */
export const getAllHashParams = () => {
  const { params } = parseHashParams();
  const result = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
};

/**
 * Build a URL from a path and a params object, skipping empty values.
 */
const buildUrl = (path, params = {}) => {
  const urlParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      urlParams.set(key, value);
    }
  });
  const searchString = urlParams.toString();
  return searchString ? `${path}?${searchString}` : path;
};

/**
 * Update the current URL's query parameters WITHOUT re-rendering the route.
 * Used to keep the address bar in sync with state (filters, page, etc.).
 */
export const updateHashUrl = (path, params = {}) => {
  const newUrl = buildUrl(path, params);
  window.history.replaceState({}, '', newUrl);
  return newUrl;
};

/**
 * Navigate to a new URL and notify React Router so the route updates
 * without a full page reload. React Router's history listens for
 * 'popstate', so we dispatch it after pushing the new entry.
 */
export const navigateToHash = (path, params = {}) => {
  const newUrl = buildUrl(path, params);
  window.history.pushState({}, '', newUrl);
  window.dispatchEvent(new PopStateEvent('popstate'));
  return newUrl;
};
